import { Component, OnDestroy, OnInit, TemplateRef, ViewChild } from '@angular/core';
import { DecimalPipe } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { ActivatedRoute, RouterLink } from '@angular/router';
import { Subject, Subscription } from 'rxjs';
import { debounceTime } from 'rxjs/operators';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { ExamService } from '../../services/exam.service';
import { SpinnerService } from '../../services/spinner.service';
import { DeviceService } from '../../services/device.service';
import { AwsService } from '../../services/aws.service';
import { ExamQuestion } from '../../models/exam-question.model';
import { ExamFile } from '../../models/exam-file.model';
import { IAnswer } from '../../models/answer.model';
import { SignatureCaptureComponent } from '../../components/signature-capture/signature-capture.component';

type ExamState = 'answering' | 'signing' | 'needsMobileDevice' | 'result';

interface DisplayResult {
  score: number;
  passed: boolean;
  correctCount?: number;
  totalCount?: number;
  passingScore?: number;
  examName?: string;
}

@Component({
  selector: 'app-exam-page',
  standalone: true,
  imports: [FormsModule, DecimalPipe, RouterLink, SignatureCaptureComponent],
  templateUrl: './exam-page.component.html',
  styleUrl: './exam-page.component.scss'
})
export class ExamPageComponent implements OnInit, OnDestroy {

  public loading: boolean = true;
  public idOrFolio: string = '';
  public examName: string = '';
  public examFiles: ExamFile[] = [];
  public questions: ExamQuestion[] = [];
  public answers: Map<string, IAnswer> = new Map();
  public isSubmitting: boolean = false;
  public isSigning: boolean = false;
  public previewImageUrl: string = '';
  public state: ExamState = 'answering';
  public displayResult: DisplayResult | null = null;

  @ViewChild('confirmModal') confirmModalRef!: TemplateRef<unknown>;
  @ViewChild('imagePreviewModal') imagePreviewModalRef!: TemplateRef<unknown>;

  private numericSubject = new Subject<{ idQuestion: string; value: number | null }>();
  private numericSub?: Subscription;

  constructor(
    private route: ActivatedRoute,
    private examService: ExamService,
    public spinnerService: SpinnerService,
    private deviceService: DeviceService,
    private awsService: AwsService,
    private modalService: NgbModal,
  ) {}

  get answeredCount(): number {
    let count = 0;
    for(const q of this.questions) {
      const ans = this.answers.get(q.idQuestion);
      if(!ans) continue;
      if((ans.idQuestionOptions && ans.idQuestionOptions.length > 0) || ans.numericValue != null) {
        count++;
      }
    }
    return count;
  }

  get progressPercent(): number {
    if(this.questions.length === 0) return 0;
    return Math.round(this.answeredCount / this.questions.length * 100);
  }

  ngOnInit(): void {

    this.numericSub = this.numericSubject.pipe(
      debounceTime(800)
    ).subscribe(async ({ idQuestion, value }) => {
      const answer: IAnswer = {
        idQuestion,
        idQuestionOptions: [],
        numericValue: value ?? undefined,
      };
      this.answers.set(idQuestion, answer);
      await this.examService.saveAnswer(this.idOrFolio, answer);
    });

    this.route.params.subscribe(async params => {
      this.idOrFolio = params['idOrFolio'];
      await this.loadQuestions();
    });

  }

  ngOnDestroy(): void {
    this.numericSub?.unsubscribe();
  }

  async loadQuestions(): Promise<void> {

    this.loading = true;
    this.spinnerService.loading = true;
    this.spinnerService.message = 'Cargando examen...';

    try {
      const instanceData = await this.examService.getInstance(this.idOrFolio);

      if(instanceData) {
        if(instanceData.examAttempt?.status === 'finished') {
          const totalCount = instanceData.questions.length;
          const score = instanceData.examAttempt?.score ?? 0;
          const correctCount = Math.round(score / 100 * totalCount);
          this.displayResult = {
            score,
            passed: instanceData.examAttempt?.passed ?? false,
            passingScore: instanceData.certificate.exam.passingScore ?? undefined,
            correctCount,
            totalCount,
            examName: instanceData.certificate.exam.name,
          };

          if(instanceData.certificate.idSignHandledBy !== null) {
            this.state = 'result';
          } else {
            this.transitionToSignOrResult();
          }
          return;
        }
        this.examName = instanceData.certificate.exam.name;
        this.examFiles = instanceData.certificate.exam.files;
        this.questions = instanceData.questions;
        for(const q of this.questions) {
          if(q.savedAnswer) {
            this.answers.set(q.idQuestion, {
              idQuestion: q.idQuestion,
              idQuestionOptions: q.savedAnswer.idQuestionOptions ?? [],
              numericValue: q.savedAnswer.numericValue ?? undefined,
            });
          }
        }
      }
    } finally {
      this.loading = false;
      this.spinnerService.loading = false;
      this.spinnerService.message = '';
    }

  }

  isSingleSelected(question: ExamQuestion, optionId: string): boolean {
    const answer = this.answers.get(question.idQuestion);
    return answer?.idQuestionOptions?.includes(optionId) ?? false;
  }

  isMultipleSelected(question: ExamQuestion, optionId: string): boolean {
    const answer = this.answers.get(question.idQuestion);
    return answer?.idQuestionOptions?.includes(optionId) ?? false;
  }

  async onSingleSelect(question: ExamQuestion, optionId: string): Promise<void> {
    const answer: IAnswer = {
      idQuestion: question.idQuestion,
      idQuestionOptions: [optionId],
    };
    this.answers.set(question.idQuestion, answer);
    await this.examService.saveAnswer(this.idOrFolio, answer);
  }

  async onMultipleToggle(question: ExamQuestion, optionId: string): Promise<void> {
    const current = this.answers.get(question.idQuestion);
    const selected = current?.idQuestionOptions ? [...current.idQuestionOptions] : [];

    const idx = selected.indexOf(optionId);
    if(idx === -1) {
      selected.push(optionId);
    } else {
      selected.splice(idx, 1);
    }

    const answer: IAnswer = {
      idQuestion: question.idQuestion,
      idQuestionOptions: selected,
    };
    this.answers.set(question.idQuestion, answer);
    await this.examService.saveAnswer(this.idOrFolio, answer);
  }

  getNumericValue(question: ExamQuestion): number | null {
    const ans = this.answers.get(question.idQuestion);
    return ans?.numericValue ?? null;
  }

  onNumericChange(question: ExamQuestion, value: number | null): void {
    this.numericSubject.next({
      idQuestion: question.idQuestion,
      value,
    });
  }

  openConfirmModal(): void {
    this.modalService.open(this.confirmModalRef, { centered: true });
  }

  openImageModal(url: string): void {
    this.previewImageUrl = url;
    this.modalService.open(this.imagePreviewModalRef, {
      size: 'xl',
      centered: true,
      windowClass: 'img-preview-modal',
    });
  }

  async onFinalize(): Promise<void> {
    if(this.isSubmitting) return;
    this.isSubmitting = true;

    this.spinnerService.loading = true;
    this.spinnerService.message = 'Enviando examen...';

    const result = await this.examService.submit(this.idOrFolio);

    this.spinnerService.loading = false;
    this.spinnerService.message = '';
    this.isSubmitting = false;

    if(result) {
      this.displayResult = {
        score: result.score,
        passed: result.passed,
        correctCount: result.correctCount,
        totalCount: result.totalCount,
        passingScore: result.passingScore,
        examName: this.examName,
      };
      this.transitionToSignOrResult();
    }
  }

  async onSignatureConfirmed(blob: Blob): Promise<void> {
    if(this.isSigning) return;
    this.isSigning = true;

    const presign = await this.examService.createSignaturePresign(this.idOrFolio);

    if(!presign) {
      this.isSigning = false;
      return;
    }

    const uploaded = await this.awsService.uploadFile(
      presign.uploadUrl,
      blob,
      'image/png',
      'Guardando firma...',
    );

    this.isSigning = false;

    if(uploaded) {
      this.state = 'result';
    }
  }

  private transitionToSignOrResult(): void {
    if(this.deviceService.isTouchDevice) {
      this.state = 'signing';
    } else {
      this.state = 'needsMobileDevice';
    }
  }

}
